import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { apiRequest } from "../auth/AdminAuthContext";
import { useSiteContent } from "../content/SiteContentContext";

const sectionFields = [
  {
    title: "Header labels",
    fields: [
      ["header_jobs_label", "Jobs link"],
      ["header_blog_label", "Blog link"],
      ["header_gallery_label", "Gallery link"],
      ["header_call_label", "Call button"],
      ["header_apply_label", "Apply button"],
      ["header_post_label", "Post job button"],
    ],
  },
  {
    title: "Hero section",
    fields: [
      ["hero_kicker", "Kicker"],
      ["hero_heading", "Heading"],
      ["hero_highlight", "Highlighted heading"],
      ["hero_background_url", "Background image URL", "url"],
      ["find_jobs_label", "Find jobs button"],
      ["hiring_label", "Hiring button"],
      ["hero_description", "Description", "textarea"],
    ],
  },
  {
    title: "Trust and opportunities",
    fields: [
      ["trust_badge_one", "Trust badge one"],
      ["trust_badge_two", "Trust badge two"],
      ["search_placeholder", "Search placeholder"],
      ["opportunities_eyebrow", "Opportunities eyebrow"],
      ["opportunities_heading", "Opportunities heading"],
      ["job_view_label", "Job card button label"],
      ["pathways_eyebrow", "Pathways eyebrow"],
      ["pathways_heading", "Pathways heading"],
    ],
  },
  {
    title: "Industry partner section",
    fields: [
      ["partner_eyebrow", "Eyebrow"],
      ["partner_heading", "Heading"],
      ["partner_button_label", "Button label"],
      ["partner_description", "Description", "textarea"],
    ],
  },
  {
    title: "Latest Blog and footer",
    fields: [
      ["blog_eyebrow", "Blog eyebrow"],
      ["blog_heading", "Blog heading"],
      ["blog_button_label", "Blog button label"],
      ["blog_read_label", "Blog card button label"],
      ["footer_tagline", "Footer tagline"],
      ["footer_address_heading", "Footer address heading"],
      ["footer_contact_heading", "Footer contact heading"],
      ["footer_website_heading", "Footer website heading"],
    ],
  },
];

function ContentField({ field, value, onChange }) {
  const [key, label, type = "text"] = field;
  return <label className={type === "textarea" ? "admin-form-full" : ""}>{label}{type === "textarea" ? <textarea required value={value || ""} onChange={(event) => onChange(key, event.target.value)} data-testid={`content-${key}-input`} /> : <input required type={type} value={value || ""} onChange={(event) => onChange(key, event.target.value)} data-testid={`content-${key}-input`} />}</label>;
}

export default function AdminHomepageContentManager({ content, onChanged, onNotice }) {
  const [form, setForm] = useState(content);
  const [saving, setSaving] = useState(false);
  const { refreshContent } = useSiteContent();

  useEffect(() => setForm(content), [content]);

  const updateField = (key, value) => setForm({ ...form, [key]: value });
  const updateCategory = (index, key, value) => {
    const categories = form.categories.map((category, currentIndex) => currentIndex === index ? { ...category, [key]: value } : category);
    setForm({ ...form, categories });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await apiRequest("/admin/content/homepage", { method: "PUT", body: JSON.stringify(form) });
      await Promise.all([onChanged(), refreshContent()]);
      onNotice("Homepage content updated.");
    } catch (error) {
      onNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <section className="admin-panel" data-testid="admin-content-loading">Loading content…</section>;
  return <section className="admin-panel" data-testid="admin-content-manager"><div className="admin-panel-heading"><div><h2>Homepage Content Manager</h2><p>Update every homepage label, message, image, badge, and section heading.</p></div></div><form className="content-manager-form" onSubmit={save} data-testid="admin-content-form">{sectionFields.map((section) => <fieldset className="content-manager-section" key={section.title}><legend>{section.title}</legend><div className="admin-form-grid">{section.fields.map((field) => <ContentField field={field} value={form[field[0]]} onChange={updateField} key={field[0]} />)}</div></fieldset>)}<fieldset className="content-manager-section"><legend>Pathway category cards</legend><div className="content-category-grid">{form.categories.map((category, index) => <div className="content-category-editor" key={`${category.label}-${index}`} data-testid={`content-category-${index}`}><strong>Card {index + 1}</strong><label>Label<input required value={category.label} onChange={(event) => updateCategory(index, "label", event.target.value)} data-testid={`content-category-${index}-label-input`} /></label><label>Hindi label<input value={category.hindi_label} onChange={(event) => updateCategory(index, "hindi_label", event.target.value)} data-testid={`content-category-${index}-hindi-input`} /></label><label>Image URL<input required type="url" value={category.image_url} onChange={(event) => updateCategory(index, "image_url", event.target.value)} data-testid={`content-category-${index}-image-input`} /></label></div>)}</div></fieldset><button className="primary-button" disabled={saving} data-testid="admin-content-save-button"><Save size={17} /> {saving ? "Saving…" : "Save homepage content"}</button></form></section>;
}